import { createRouter, createWebHistory } from "vue-router";
import Home from "@/pages/Home.vue";
import { useAuthStore } from "@/stores/auth";
import { getActiveEventSlug } from "@/composables/useActiveEvent";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/", name: "home", component: Home },
    { path: "/select-event", name: "select-event",
      component: () => import("@/pages/SelectEvent.vue") },
    { path: "/consent", name: "consent",
      component: () => import("@/pages/Consent.vue") },
    { path: "/me", name: "me",
      component: () => import("@/pages/Me.vue") },
    { path: "/me/profile", name: "me-profile",
      component: () => import("@/pages/MeProfile.vue") },
    { path: "/me/transactions", name: "me-transactions",
      component: () => import("@/pages/MeTransactions.vue") },
    // /me/lottery is now folded into /rewards (which shows both the user's
    // personal result and the full reward catalogue).
    { path: "/me/lottery", redirect: "/rewards" },
    { path: "/bars", name: "bars",
      component: () => import("@/pages/Bars.vue") },
    { path: "/bars/:id", name: "bar-detail",
      component: () => import("@/pages/BarDetail.vue") },
    { path: "/sponsors/:id", name: "sponsor-detail",
      component: () => import("@/pages/Sponsor.vue") },
    { path: "/map", name: "map",
      component: () => import("@/pages/Map.vue") },
    { path: "/rules", name: "rules",
      component: () => import("@/pages/Rules.vue") },
    { path: "/rewards", name: "rewards",
      component: () => import("@/pages/Rewards.vue") },
    // Optional `:cups` segment selects the tier (+1 / +2 / +3 點).
    // Old URLs `/checkin/{bar_id}` keep working — drink_count defaults to 1.
    { path: "/checkin/:barId/:cups?", name: "checkin",
      component: () => import("@/pages/Checkin.vue") },
    { path: "/wall", name: "wall",
      component: () => import("@/pages/Wall.vue") },
    { path: "/wallet", name: "wallet",
      component: () => import("@/pages/Wallet.vue") },
    { path: "/tos", name: "tos",
      component: () => import("@/pages/Tos.vue") },
    { path: "/privacy", name: "privacy",
      component: () => import("@/pages/Privacy.vue") },
  ],
});

const PUBLIC_ROUTES = new Set([
  "home", "rules", "rewards", "bars", "bar-detail", "sponsor-detail", "map",
  "tos", "privacy",
]);

router.beforeEach(async (to) => {
  // Cold entry: the chooser needs no auth/event. Any other route without a
  // bound event → send to the chooser (boot resolver already auto-binds the
  // single-active case, so reaching here means 0 or 2+ active).
  if (to.name === "select-event") return true;
  if (!getActiveEventSlug()) return { name: "select-event" };
  const auth = useAuthStore();
  if (PUBLIC_ROUTES.has(to.name as string)) return true;
  if (!auth.isLoggedIn) {
    // Preserve LIFF deep-link target across the LINE login round-trip so
    // returning users land on /checkin/:id (or wherever) instead of /me.
    if (to.path.startsWith("/checkin/")) {
      sessionStorage.setItem("pendingDeepLink", to.fullPath);
    }
    return { name: "home" };
  }
  // Hydrate `user` from /api/me on first navigation after reload
  if (auth.user === null) {
    await auth.fetchMe();
  }
  // Consent gate first — when consent is required, /consent is the only
  // page allowed; even /me/profile is gated behind it.
  if (auth.user?.requires_consent) {
    return to.name === "consent" ? true : { name: "consent" };
  }
  if (auth.user?.requires_profile) {
    return to.name === "me-profile" ? true : { name: "me-profile" };
  }
  return true;
});

export default router;
